// Pure model of the policies form: the values, the check of a guest domain (with the contract's own
// domain rule) and the PATCH body made of the changed policies only.
import type {
  OrganizationPoliciesDtoOutput,
  UpdatePoliciesDto,
  UpdatePoliciesDtoSecurity,
  UpdatePoliciesDtoSharing,
} from '@batiflow/api-client';
import { SharingPolicySchema } from '@batiflow/contracts';

export const MAX_GUEST_DOMAINS = 50;
/** Session lifetimes offered besides "platform default" (SecurityPolicySchema: 1 to 12 hours). */
export const SESSION_HOURS: readonly number[] = Array.from({ length: 12 }, (_unused, i) => i + 1);

export interface PolicyValues {
  requireApprovalForExternalSpaces: boolean;
  projectManagersCanShare: boolean;
  allowedGuestDomains: string[];
  /** '' = the platform default, else '1' to '12' (a select's value). */
  sessionHours: string;
  mfaForAllMembers: boolean;
}

export function valuesOf(policies: OrganizationPoliciesDtoOutput): PolicyValues {
  return {
    requireApprovalForExternalSpaces: policies.sharing.requireApprovalForExternalSpaces,
    projectManagersCanShare: policies.sharing.projectManagersCanShare,
    allowedGuestDomains: [...policies.sharing.allowedGuestDomains],
    sessionHours:
      policies.security.sessionAbsoluteHours === null
        ? ''
        : String(policies.security.sessionAbsoluteHours),
    mfaForAllMembers: policies.security.mfaForAllMembers,
  };
}

export type DomainCheck =
  { ok: true; domain: string } | { ok: false; reason: 'empty' | 'invalid' | 'duplicate' | 'limit' };

const DOMAIN_SCHEMA = SharingPolicySchema.shape.allowedGuestDomains.element;

/**
 * Checks a domain typed by the user. A leading "@" (as in an e-mail address) is accepted and
 * dropped; the contract's rule (lower case, labels, a letter-only extension) decides the rest.
 */
export function checkDomain(input: string, existing: readonly string[]): DomainCheck {
  const cleaned = input.trim().replace(/^@+/, '');
  if (!cleaned) return { ok: false, reason: 'empty' };
  const parsed = DOMAIN_SCHEMA.safeParse(cleaned);
  if (!parsed.success) return { ok: false, reason: 'invalid' };
  if (existing.includes(parsed.data)) return { ok: false, reason: 'duplicate' };
  if (existing.length >= MAX_GUEST_DOMAINS) return { ok: false, reason: 'limit' };
  return { ok: true, domain: parsed.data };
}

/** Hours of the select value, null for the platform default. */
export function hoursOf(value: string): number | null {
  const hours = Number(value);
  return value !== '' && SESSION_HOURS.includes(hours) ? hours : null;
}

/** The policies that differ from the saved ones; both groups are absent when nothing changed. */
export function buildPoliciesPatch(
  saved: OrganizationPoliciesDtoOutput,
  values: PolicyValues,
): UpdatePoliciesDto {
  const sharing: UpdatePoliciesDtoSharing = {};
  if (values.requireApprovalForExternalSpaces !== saved.sharing.requireApprovalForExternalSpaces)
    sharing.requireApprovalForExternalSpaces = values.requireApprovalForExternalSpaces;
  if (values.projectManagersCanShare !== saved.sharing.projectManagersCanShare)
    sharing.projectManagersCanShare = values.projectManagersCanShare;
  if (values.allowedGuestDomains.join() !== saved.sharing.allowedGuestDomains.join())
    sharing.allowedGuestDomains = values.allowedGuestDomains;

  const security: UpdatePoliciesDtoSecurity = {};
  if (hoursOf(values.sessionHours) !== saved.security.sessionAbsoluteHours)
    security.sessionAbsoluteHours = hoursOf(values.sessionHours);
  if (values.mfaForAllMembers !== saved.security.mfaForAllMembers)
    security.mfaForAllMembers = values.mfaForAllMembers;

  return {
    ...(Object.keys(sharing).length > 0 ? { sharing } : {}),
    ...(Object.keys(security).length > 0 ? { security } : {}),
  };
}
